export const TOUR_ANCHOR = {
    MY_PROJECTS: 'my-projects',
    PROJECT_STRUCTURE: 'project-structure',
    DEMO_PROJECTS: 'demo-projects',
    CREATE_PROJECT: 'create-project',
    HELP_HUB: 'help-hub',
};
const WELCOME = {
    id: 'welcome',
    title: 'Welcome to LandScope',
    body: 'In the next 60 seconds, you’ll see how to open a project, read the terrain, and start your first map. You can replay this anytime from Help & Support.',
};
const MY_PROJECTS = {
    id: 'my-projects',
    target: TOUR_ANCHOR.MY_PROJECTS,
    title: 'Your projects',
    body: 'Every map you create or unlock lives here. Click any project to load it on the map and pick up where you left off.',
};
const PROJECT_STRUCTURE = {
    id: 'project-structure',
    target: TOUR_ANCHOR.PROJECT_STRUCTURE,
    title: 'Layers & folders',
    body: 'This is where an open project’s layers live. Toggle them on and off to see slope, water, access, and more. Click “?” on any layer for a quick explanation.',
};
const DEMO_PROJECTS = {
    id: 'demo-projects',
    target: TOUR_ANCHOR.DEMO_PROJECTS,
    title: 'Explore a sample project',
    body: 'Start here. Open a sample project to see a real property with contours, drainage, and access already mapped. It’s the fastest way to see what LandScope can do.',
};
const CREATE_PROJECT = {
    id: 'create-project',
    target: TOUR_ANCHOR.CREATE_PROJECT,
    title: 'Start your own project',
    body: 'Ready to map your area of interest? Click “Create Project” to get started. Your first demonstration project is free for 7 days. Instant access is available across the US, Canada, the UK, New Zealand, the Netherlands, France, Switzerland, Norway, Austria, Portugal and Uruguay. Turn on the coverage layer to check yours.',
};
const HELP_HUB = {
    id: 'help-hub',
    target: TOUR_ANCHOR.HELP_HUB,
    title: 'We’re here for you',
    body: 'Stuck or curious? Open Help & Support for the layer guide, short tutorials, and a direct way to reach us.',
};
const FINISH = {
    id: 'finish',
    title: 'Ready to dive in',
    body: 'Step 1: Open a sample project to see LandScope in action. Step 2: Create your own project when you’re ready. Welcome aboard.',
};
const CONSULTANT_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to LandScope',
        body: "A quick look at how LandScope turns a client's site into terrain you can hand over: shared views, printed sheets and CAD-ready exports. Replay this anytime from Help & Support.",
    },
    {
        id: 'my-projects',
        target: TOUR_ANCHOR.MY_PROJECTS,
        title: 'One project per client site',
        body: 'Every client site you map lives here, each with its own layers, notes and markup. Click one to load it and pick up where you left off.',
    },
    PROJECT_STRUCTURE,
    {
        id: 'deliverables',
        title: 'Client-ready output',
        body: 'From any project, print a sheet with a legend and source credit, export contours as DXF for CAD, or export a KMZ for Google Earth.',
    },
    {
        id: 'client-viewers',
        title: 'Share it with your client',
        body: 'Once a project is unlocked, invite your client as a viewer. Viewers see the map and can comment, and they cost nothing.',
    },
    {
        id: 'create-project',
        target: TOUR_ANCHOR.CREATE_PROJECT,
        title: "Start with a client's site",
        body: 'Click “Create Project” and draw the site boundary. Your first demonstration project is free for 7 days.',
    },
    HELP_HUB,
];
const DEVELOPER_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to LandScope',
        body: 'A quick look at how to read a parcel before you commit to it: slope, aspect, drainage and how much ground one project covers. Replay this anytime from Help & Support.',
    },
    {
        id: 'demo-projects',
        target: TOUR_ANCHOR.DEMO_PROJECTS,
        title: 'See a parcel already read',
        body: 'Open a sample project to see slope, aspect and drainage on real ground before you run your own.',
    },
    PROJECT_STRUCTURE,
    {
        id: 'slope-aspect',
        title: 'Slope and aspect first',
        body: 'Turn on Slope to see usable ground at a glance and Aspect to see which way it faces. The drainage layers show where water collects across the site.',
    },
    {
        id: 'create-project',
        target: TOUR_ANCHOR.CREATE_PROJECT,
        title: 'Run your first parcel',
        body: 'Click “Create Project” and draw the parcel. The area readout tells you as you draw whether it fits in one project; larger holdings run as more than one.',
    },
    {
        id: 'my-projects',
        target: TOUR_ANCHOR.MY_PROJECTS,
        title: 'Every parcel in one place',
        body: 'Each parcel you run lands here, so you can move between sites and come back to any of them.',
    },
    HELP_HUB,
];
const OWNER_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to LandScope',
        body: 'A quick look at how to see your own land: its contours, where the water goes, and how to sketch your plans on top. Replay this anytime from Help & Support.',
    },
    {
        id: 'demo-projects',
        target: TOUR_ANCHOR.DEMO_PROJECTS,
        title: 'See a property already mapped',
        body: 'Open a sample project to see contours and drainage on a real property.',
    },
    PROJECT_STRUCTURE,
    {
        id: 'contours-water',
        title: 'Contours and water',
        body: 'Contours show the shape of your ground. The drainage layers show where water runs and where it collects after rain.',
    },
    {
        id: 'sketch-print',
        title: 'Sketch it, then print it',
        body: 'Mark up the map with sketches, shapes, paths and labels, then print a sheet for the kitchen table.',
    },
    {
        id: 'create-project',
        target: TOUR_ANCHOR.CREATE_PROJECT,
        title: 'Map your property',
        body: 'Click “Create Project” and draw your boundary. Your first demonstration project is free for 7 days.',
    },
    HELP_HUB,
];
const BUYER_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to LandScope',
        body: 'A quick look at how to check a piece of land before you make an offer: its slope, its drainage and its terrain stats. Replay this anytime from Help & Support.',
    },
    MY_PROJECTS,
    {
        id: 'demo-projects',
        target: TOUR_ANCHOR.DEMO_PROJECTS,
        title: 'See a property already checked',
        body: 'Open a sample project to see what a terrain read looks like on real ground.',
    },
    PROJECT_STRUCTURE,
    {
        id: 'before-offer',
        title: 'What to check before an offer',
        body: 'Start with Slope for usable ground, the drainage layers for wet spots, and the terrain statistics for elevation and slope figures you can quote.',
    },
    {
        id: 'create-project',
        target: TOUR_ANCHOR.CREATE_PROJECT,
        title: "Check the land you're looking at",
        body: 'Click “Create Project” and draw the property. Your first demonstration project is in full detail for 7 days, so map it while you are still deciding.',
    },
    HELP_HUB,
];
const DEFAULT_STEPS = [
    WELCOME,
    MY_PROJECTS,
    PROJECT_STRUCTURE,
    DEMO_PROJECTS,
    CREATE_PROJECT,
    HELP_HUB,
    FINISH,
];
export const TOUR_STEP_SETS = {
    consultant: CONSULTANT_STEPS,
    developer: DEVELOPER_STEPS,
    owner: OWNER_STEPS,
    buyer: BUYER_STEPS,
    default: DEFAULT_STEPS,
};
export const tourStepsFor = (avatar) => TOUR_STEP_SETS[avatar];
